import React, { useState, useEffect } from 'react';
import './App.css';
import { AppLayout, SideNavigation, TopNavigation } from '@awsui/components-react';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import { ContentGeneratorComponent } from './components/Content/ContentGeneratorComponent';
import { MyResearchComponent } from './components/Content/MyResearchComponent';
import { LLMPlaygroundComponent } from './components/CI Playground/LLMPlaygroundComponent';
import { AnnouncementsPanel } from './components/Compete Trends/AnnouncementsPanel';
import { ObservationsPanel } from './components/Compete Trends/ObservationsPanel';
import { TrendsPanel } from './components/Compete Trends/TrendsPanel';
import ChatInterface from './components/Chat AI/ChatInterface';
import PromptLibrary from './components/Prompt Library/PromptLibrary';
import { getCookie, parseJwt, isTokenExpiringSoon, refreshTokens } from './utils/auth';


const HomePage = () => (
  <div>
    <h1>Welcome to Cloud Intelligence Apps</h1>
    <p>Select an application from the navigation menu to get started.</p>
  </div>
);

// Helper function to set cookies with expiration
const setCookie = (name, value, expiryHours = 24) => {
  const date = new Date();
  date.setTime(date.getTime() + (expiryHours * 60 * 60 * 1000));
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=/;secure;samesite=strict";
};

// Define which groups can access which sections
const sectionPermissions = {
  'Chat AI': ['admin', 'ci_users_excluding_gcr', 'us-east-1_R3nP1Lpjz_AmazonFederate'],
  'Compete Trends': ['admin'],
  'Content': ['admin', 'ci_users_excluding_gcr'],
  'CI Playground': ['admin', 'ci_users_excluding_gcr']
};

// Permission check for individual components
const componentPermissions = {
  // Chat AI components
  'chat': ['admin', 'ci_users_excluding_gcr', 'us-east-1_R3nP1Lpjz_AmazonFederate'],
  'prompt-library': ['admin', 'ci_users_excluding_gcr', 'us-east-1_R3nP1Lpjz_AmazonFederate'],

  // Compete Trends components
  'announcements': ['admin'],
  'observations': ['admin'],
  'trends': ['admin'],

  // Content components
  'content': ['admin', 'ci_users_excluding_gcr'],
  'my-research': ['admin', 'ci_users_excluding_gcr'],

  // CI Playground components
  'llm': ['admin'],
};

// Helper function to check if user has access to a section
const hasAccessToSection = (sectionName, userGroups) => {
  const requiredGroups = sectionPermissions[sectionName] || [];

  // If no groups specified, everyone can access
  if (requiredGroups.length === 0) return true;

  // Check if user belongs to any of the required groups
  return userGroups.some(group => requiredGroups.includes(group));
};

// Helper function to check if user has access to a component
const hasAccessToComponent = (componentId, userGroups) => {
  const requiredGroups = componentPermissions[componentId] || [];

  // If no groups specified, everyone can access
  if (requiredGroups.length === 0) return true;

  // Check if user belongs to any of the required groups
  return userGroups.some(group => requiredGroups.includes(group));
};

// Protected component wrapper
const ProtectedComponent = ({ componentId, userGroups, fallback = null, children }) => {
  const hasAccess = hasAccessToComponent(componentId, userGroups);
  return hasAccess ? children : (fallback || <AccessDenied />);
};

// Access denied component
const AccessDenied = () => (
  <div style={{ textAlign: 'center', padding: '2rem', color: '#d13212' }}>
    <h2>Access Denied</h2>
    <p>You don't have permission to access this feature.</p>
    <p>Please contact your administrator if you believe this is an error.</p>
  </div>
);

function App() {
  const [activeComponent, setActiveComponent] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userGroups, setUserGroups] = useState([]);
  const [userName, setUserName] = useState('');

  // Apply theme based on system preference
  useEffect(() => {
    const applyTheme = (isDark) => {
      applyMode(isDark ? Mode.Dark : Mode.Light);
    };

    // Check initial preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    applyTheme(mediaQuery.matches);

    // Listen for changes
    const handleChange = (e) => applyTheme(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have a code in the URL (from Midway redirect)
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        if (code) {
          // Exchange the code for tokens with Cognito
          const requestBody = {
            code: code,
            redirect_uri: window.location.origin
          };

          const tokenExchangeUrl = process.env.REACT_APP_TOKEN_EXCHANGE_URL || 'https://k4c8bxuzue.execute-api.us-east-1.amazonaws.com/prod/GetCognitoClientSecret';
          const tokenResponse = await fetch(tokenExchangeUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });

          if (tokenResponse.ok) {
            const responseData = await tokenResponse.json();

            // Parse the inner JSON body if it's a string
            let tokens;
            if (typeof responseData === 'object' && responseData.body) {
              if (typeof responseData.body === 'string') {
                tokens = JSON.parse(responseData.body);
              } else {
                tokens = responseData.body;
              }
            } else {
              tokens = responseData;
            }

            // Store tokens in cookies (expires in 24 hours to match Cognito token validity)
            setCookie('accessToken', tokens.access_token, 24);
            setCookie('idToken', tokens.id_token, 24);
            setCookie('refreshToken', tokens.refresh_token, 24);

            // Extract user groups and other info from the ID token
            const decodedToken = parseJwt(tokens.id_token);
            if (decodedToken) {
              // Set user groups from token
              if (decodedToken['cognito:groups']) {
                console.log('User groups:', decodedToken['cognito:groups']);
                setUserGroups(decodedToken['cognito:groups']);
              }

              // Set user name for display purposes
              if (decodedToken.name) {
                setUserName(decodedToken.name);
              } else if (decodedToken.email) {
                setUserName(decodedToken.email);
              }
            }

            // Remove code from URL after successful token exchange
            window.history.replaceState({}, document.title, window.location.pathname);

            setIsAuthenticated(true);
          } else {
            const errorText = await tokenResponse.text();
            console.error('Token exchange failed:', tokenResponse.status, errorText);
            throw new Error(`Failed to exchange code for tokens: ${tokenResponse.status}`);
          }
        } else {
          // Check if we have valid tokens in cookies
          const accessToken = getCookie('accessToken');
          const idToken = getCookie('idToken');

          if (accessToken && idToken) {
            // Token exists, consider user authenticated
            // Also extract user groups from the ID token
            const decodedToken = parseJwt(idToken);
            if (decodedToken) {
              if (decodedToken['cognito:groups']) {
                setUserGroups(decodedToken['cognito:groups']);
              }

              if (decodedToken.name) {
                setUserName(decodedToken.name);
              } else if (decodedToken.email) {
                setUserName(decodedToken.email);
              }
            }

            setIsAuthenticated(true);
          } else {
            // No token, redirect to Midway
            redirectToMidway();
          }
        }
      } catch (error) {
        console.error('Authentication error:', error);
        redirectToMidway();
      } finally {
        setIsLoading(false);
        // Clear the redirecting flag when we're done
        if (getCookie('accessToken')) {
          sessionStorage.removeItem('redirecting');
        }
      }
    };

    const redirectToMidway = () => {
      // Prevent redirect loops
      if (sessionStorage.getItem('redirecting')) {
        console.log('Already redirecting, preventing loop');
        return;
      }

      sessionStorage.setItem('redirecting', 'true');
      const redirectUri = encodeURIComponent(window.location.origin);
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '2j7n6qlp5v84krflbvan0s9akr';
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || 'us-east-1xha300vyy.auth.us-east-1.amazoncognito.com';
      const midwayUrl = `https://${cognitoDomain}/login?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
      console.log('Redirecting to:', midwayUrl);

      // Open in current window
      window.location.href = midwayUrl;
    };

    checkAuth();
  }, []);

  // Auto-refresh tokens before they expire
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshInterval = setInterval(async () => {
      if (isTokenExpiringSoon(30)) { // Refresh if expiring within 30 min
        try {
          await refreshTokens();
          console.log('Tokens refreshed silently');
          // Dispatch custom event to notify WebSocket services
          window.dispatchEvent(new CustomEvent('tokenRefreshed'));
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Redirect to login on refresh failure
          sessionStorage.removeItem('redirecting');
          window.location.href = window.location.origin;
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Only render the app if authenticated
  if (!isAuthenticated) {
    return null; // This will never actually render because we redirect in redirectToMidway
  }

  // Base navigation items structure
  const baseNavItems = [
    {
      type: 'section',
      text: 'Chat AI',
      items: [
        {
          type: 'link',
          text: 'Chat Interface',
          href: '#chat',
          active: activeComponent === 'chat',
          onFollow: () => setActiveComponent('chat'),
          componentId: 'chat'
        },
        {
          type: 'link',
          text: 'Prompt Library',
          href: '#prompt-library',
          active: activeComponent === 'prompt-library',
          onFollow: () => setActiveComponent('prompt-library'),
          componentId: 'prompt-library'
        }
      ]
    },
    {
      type: 'section',
      text: 'Compete Trends',
      items: [
        {
          type: 'link',
          text: 'Observations',
          href: '#observations',
          active: activeComponent === 'observations',
          onFollow: () => setActiveComponent('observations'),
          componentId: 'observations'
        },
        {
          type: 'link',
          text: 'Trends',
          href: '#trends',
          active: activeComponent === 'trends',
          onFollow: () => setActiveComponent('trends'),
          componentId: 'trends'
        }
      ]
    },
    {
      type: 'section',
      text: 'Content',
      items: [
        {
          type: 'link',
          text: 'Battlecard Generator',
          href: '#content',
          active: activeComponent === 'content',
          onFollow: () => setActiveComponent('content'),
          componentId: 'content'
        },
        {
          type: 'link',
          text: 'My Research',
          href: '#my-research',
          active: activeComponent === 'my-research',
          onFollow: () => setActiveComponent('my-research'),
          componentId: 'my-research'
        }
      ]
    },
    {
      type: 'section',
      text: 'CI Playground',
      items: [
        {
          type: 'link',
          text: 'CI Playground',
          href: '#llm',
          active: activeComponent === 'llm',
          onFollow: () => setActiveComponent('llm'),
          componentId: 'llm'
        }
      ]
    }
  ];

  // Filter navigation items based on user permissions
  const getFilteredNavItems = () => {
    return baseNavItems
      // Filter sections based on permissions
      .filter(section => hasAccessToSection(section.text, userGroups))
      // For each remaining section, filter its items
      .map(section => ({
        ...section,
        items: section.items.filter(item =>
          hasAccessToComponent(item.componentId, userGroups)
        )
      }))
      // Remove any sections that now have no items
      .filter(section => section.items.length > 0);
  };

  const navigationContent = (
    <SideNavigation
      header={{ text: 'CI Apps', href: '#home' }}
      items={getFilteredNavItems()}
      onFollow={event => {
        event.preventDefault();
        const href = event.detail.href;
        if (href === '#chat') {
          setActiveComponent('chat');
        } else if (href === '#prompt-library') {
          setActiveComponent('prompt-library');
        } else if (href === '#announcements') {
          setActiveComponent('announcements');
        } else if (href === '#observations') {
          setActiveComponent('observations');
        } else if (href === '#trends') {
          setActiveComponent('trends');
        } else if (href === '#content') {
          setActiveComponent('content');
        } else if (href === '#my-research') {
          setActiveComponent('my-research');
        } else if (href === '#llm') {
          setActiveComponent('llm');
        } else if (href === '#home') {
          setActiveComponent('');
        }
      }}
    />
  );

  // Render the active component based on selection with permissions check
  const renderActiveComponent = () => {
    switch (activeComponent) {
      case 'chat':
        return (
          <ProtectedComponent componentId="chat" userGroups={userGroups}>
            <ChatInterface />
          </ProtectedComponent>
        );
      case 'prompt-library':
        return (
          <ProtectedComponent componentId="prompt-library" userGroups={userGroups}>
            <PromptLibrary />
          </ProtectedComponent>
        );
      case 'announcements':
        return (
          <ProtectedComponent componentId="announcements" userGroups={userGroups}>
            <AnnouncementsPanel />
          </ProtectedComponent>
        );
      case 'observations':
        return (
          <ProtectedComponent componentId="observations" userGroups={userGroups}>
            <ObservationsPanel />
          </ProtectedComponent>
        );
      case 'trends':
        return (
          <ProtectedComponent componentId="trends" userGroups={userGroups}>
            <TrendsPanel />
          </ProtectedComponent>
        );
      case 'content':
        return (
          <ProtectedComponent componentId="content" userGroups={userGroups}>
            <ContentGeneratorComponent />
          </ProtectedComponent>
        );
      case 'my-research':
        return (
          <ProtectedComponent componentId="my-research" userGroups={userGroups}>
            <MyResearchComponent />
          </ProtectedComponent>
        );
      case 'llm':
        return (
          <ProtectedComponent componentId="llm" userGroups={userGroups}>
            <LLMPlaygroundComponent />
          </ProtectedComponent>
        );
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="App">
      <div id="header" style={{ position: 'sticky', top: 0, zIndex: 1002 }}>
        <TopNavigation
          identity={{
            href: "#",
            title: "Cloud Intelligence Apps",
            onFollow: (e) => {
              e.preventDefault();
              setActiveComponent('home');
            }
          }}
          utilities={[
            {
              type: "button",
              text: userName || "User",
              iconName: "user-profile",
              variant: "link",
              disableUtilityCollapse: true
            }
          ]}
        />
      </div>
      <AppLayout
        navigation={navigationContent}
        content={renderActiveComponent()}
        navigationWidth={250}
        toolsHide={true}
        contentType="default"
        headerSelector="#header"
      />
    </div>
  );
}

export default App;
